import { useToast } from '@components/index'
import { useUpdatePartnerReward } from '@services/referral'
import { Pencil } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

interface EditPartnerModalProps {
  open: boolean
  onClose: () => void
  onSuccess?: () => void
  partnerId: string
  currentRewardAmount?: number
}

export function EditPartnerModal({
  open,
  onClose,
  onSuccess,
  partnerId,
  currentRewardAmount
}: EditPartnerModalProps) {
  const { t } = useTranslation('admin')
  const toast = useToast()
  const updateMutation = useUpdatePartnerReward()
  const [amount, setAmount] = useState('')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (open) {
      setAmount(currentRewardAmount != null ? String(currentRewardAmount) : '')
      setError(null)
    }
  }, [open, currentRewardAmount])

  const handleSubmit = async () => {
    const num = parseFloat(amount)
    if (isNaN(num) || num < 0) {
      setError(t('referrals.edit.rewardAmountRequired'))
      return
    }
    try {
      await updateMutation.mutateAsync({
        id: partnerId,
        body: { new_reward_amount: num }
      })
      toast.success({ message: t('referrals.edit.success') })
      onClose()
      onSuccess?.()
    } catch (error: any) {
      toast.error({
        message:
          error?.response?.data?.detail ||
          error?.message ||
          t('referrals.edit.error')
      })
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
    >
      <DialogContent>
        <DialogHeader>
          <div className="flex items-start gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
              <Pencil className="h-5 w-5" />
            </span>
            <div className="flex flex-col gap-1">
              <DialogTitle>{t('referrals.edit.title')}</DialogTitle>
              <DialogDescription>
                {t('referrals.edit.description')}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div>
          <Label className="mb-1.5 block">
            {t('referrals.edit.rewardAmount')}
            <span className="ml-0.5 text-destructive">*</span>
          </Label>
          <div className="relative">
            <Input
              type="number"
              min={0}
              step="0.01"
              value={amount}
              autoFocus
              onChange={(e) => {
                setAmount(e.target.value)
                setError(null)
              }}
              placeholder={t('referrals.edit.rewardAmountPlaceholder')}
              className="pr-9"
            />
            <span className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 text-muted-foreground">
              €
            </span>
          </div>
          {error && (
            <p className="mt-1.5 text-sm font-medium text-destructive">
              {error}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghostDark" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleSubmit} disabled={updateMutation.isPending}>
            {t('referrals.edit.submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
